/**
 * Phép tính thuần của màn Tóm tắt văn bản, chuyển từ `useTomTat.ts` và `data/types.ts` của foxainative:
 * trạng thái hiển thị, tỷ lệ rút gọn, chọn tệp gửi lên, tệp `.doc` xuất bản tóm tắt, và bộ đọc Markdown
 * tối giản cho bản tóm tắt AI (bản cũ dùng `react-markdown`; kho này không thêm thư viện, mà bản tóm tắt
 * chỉ có đoạn, tiêu đề, danh sách và chữ đậm).
 *
 * Chữ trong tệp `.doc` là **nội dung văn bản hành chính**, không phải chuỗi giao diện (như
 * `khung-van-ban.ts`), nên không đi qua catalog.
 */
import { thoatHtml } from './mau-van-ban.ts';
import type { ChiTietTomTat, TrangThaiTomTat } from '../types-tom-tat.ts';

/** Bản cũ chỉ có hai trạng thái hiển thị: đã tóm tắt, chưa tóm tắt (kể cả đang chạy và lỗi). */
export function daTomTat(s: TrangThaiTomTat): boolean {
  return s === 'COMPLETED';
}

/** Còn tác vụ nền đang xử lý: màn hình hỏi lại mỗi 4 giây (ADR-0071). */
export function dangXuLy(s: TrangThaiTomTat): boolean {
  return s === 'PENDING' || s === 'PROCESSING';
}

/** Tỷ lệ rút gọn làm tròn, như `toSumRow`. */
export function tyLeRutGon(ratio: number | null | undefined): number | null {
  return ratio == null ? null : Math.round(ratio);
}

/** Đuôi `.txt`: đọc luôn nội dung để người dùng xem, sửa trước khi tóm tắt. */
export function laTepChu(ten: string): boolean {
  return /\.txt$/i.test(ten);
}

/**
 * Tệp gửi `POST /summarize/file`, như `saveAdd` của bản cũ: tệp PDF/DOCX/MD gửi nguyên bản khi không dán
 * nội dung; có nội dung (dán tay hoặc đọc từ `.txt`) thì đóng gói thành `{tên}.txt`, ưu tiên bản người dùng
 * đã sửa. `null` khi thiếu cả hai.
 */
export function tepGuiLen(tieuDe: string, noiDung: string, tep: File | null): File | null {
  const chu = noiDung.trim();
  if (tep && !laTepChu(tep.name) && !chu) return tep;
  if (chu) return new File([chu], `${tieuDe}.txt`, { type: 'text/plain' });
  return tep;
}

/** Tên văn bản gợi ý từ tên tệp khi người dùng chưa nhập. */
export function tenTuTep(ten: string): string {
  return ten.replace(/\.[^.]+$/, '');
}

/** Tệp Word (HTML) của bản tóm tắt, như `exportDoc` của bản cũ. */
export function docXuatTomTat(d: Pick<ChiTietTomTat, 'document_name' | 'document_type' | 'creator_name' | 'summary_content'>): string {
  return (
    `<html xmlns:w="urn:schemas-microsoft-com:office:word"><head><meta charset="utf-8"><title>${thoatHtml(d.document_name)}</title></head>\n` +
    `<body style="font-family:'Times New Roman',serif;font-size:14pt;line-height:1.5;">\n` +
    `<h2 style="text-align:center;">BẢN TÓM TẮT VĂN BẢN</h2>\n` +
    `<p><b>Tên văn bản:</b> ${thoatHtml(d.document_name)}<br><b>Loại văn bản:</b> ${thoatHtml(d.document_type)} &nbsp;|&nbsp; <b>Người tạo:</b> ${thoatHtml(d.creator_name ?? '')}</p><hr>\n` +
    `<p>${thoatHtml(d.summary_content ?? '').replace(/\n/g, '<br>')}</p></body></html>`
  );
}

export function tenTepXuatTomTat(tenVanBan: string): string {
  return `${tenVanBan.slice(0, 60)} - Tom tat.doc`;
}

// ─── Markdown tối giản ───────────────────────────────────────────────────────

export type KhoiMarkdown =
  | { kieu: 'tieu-de'; chu: string }
  | { kieu: 'doan'; chu: string }
  | { kieu: 'ds'; thuTu: boolean; muc: string[] };

/** Chia bản tóm tắt thành khối: `#` tiêu đề, `-`/`*`/`1.` danh sách, còn lại là đoạn. */
export function docMarkdown(van: string): KhoiMarkdown[] {
  const ra: KhoiMarkdown[] = [];
  let doan: string[] = [];
  const dongDoan = () => {
    if (doan.length) ra.push({ kieu: 'doan', chu: doan.join(' ') });
    doan = [];
  };
  for (const dongGoc of van.replace(/\r/g, '').split('\n')) {
    const dong = dongGoc.trim();
    if (!dong) {
      dongDoan();
      continue;
    }
    const tieuDe = /^#{1,6}\s+(.*)$/.exec(dong);
    const khongThuTu = /^[-*+]\s+(.*)$/.exec(dong);
    const coThuTu = /^\d+[.)]\s+(.*)$/.exec(dong);
    if (tieuDe) {
      dongDoan();
      ra.push({ kieu: 'tieu-de', chu: tieuDe[1]! });
    } else if (khongThuTu || coThuTu) {
      dongDoan();
      const thuTu = !!coThuTu;
      const chu = (khongThuTu ?? coThuTu)![1]!;
      const cuoi = ra[ra.length - 1];
      if (cuoi && cuoi.kieu === 'ds' && cuoi.thuTu === thuTu) cuoi.muc.push(chu);
      else ra.push({ kieu: 'ds', thuTu, muc: [chu] });
    } else {
      doan.push(dong);
    }
  }
  dongDoan();
  return ra;
}

/** Tách `**đậm**` trong một dòng; phần còn lại là chữ thường. */
export function tachDam(dong: string): Array<{ dam: boolean; chu: string }> {
  return dong
    .split(/(\*\*[^*]+\*\*)/g)
    .filter(Boolean)
    .map((p) => (/^\*\*[^*]+\*\*$/.test(p) ? { dam: true, chu: p.slice(2, -2) } : { dam: false, chu: p }));
}
